import { useRecoilValue, useRecoilState } from 'recoil';
import useSWR from 'swr';
import format from 'date-fns/format';

import { useNotification } from 'hooks/useNotification';
import { apiInstance } from 'lib/utils/axios';
import { API_ROUTES } from 'lib/api-routes';
import { httpHeadersState, userSecretState } from 'lib/atoms/userSecretAtom';
import {
  CommunityDashboardAttributes,
  CommunityTypes,
} from 'lib/models/community';
import { getFormattedFilename } from 'utils/format';

export interface ReturnType {
  dashboardData: CommunityDashboardAttributes | undefined;
  mutateCommunityDashboard: () => void;
  onApproveSubscriptionRequests: (ids: string[]) => Promise<void>;
  onDeclineSubscriptionRequests: (ids: string[]) => Promise<void>;
  onDeleteMember: (ids: string[]) => Promise<void>;
  onCSVExport: (title: CommunityTypes) => Promise<string | undefined>;
  sendMessage: (message: string, candidate_ids: string[]) => Promise<void>;
}

export const useCommunityBoard = (): ReturnType => {
  const { headers, plainHeaders } = useRecoilValue(httpHeadersState);
  const [userSecret] = useRecoilState(userSecretState);

  const notificationInstance = useNotification();

  const { data: dashboardData, mutate: mutateCommunityDashboard } =
    useSWR<CommunityDashboardAttributes>(
      userSecret
        ? [`${API_ROUTES.ADD_SOCIETY_SUBSCRIBERS}/dashboard`, headers]
        : null,
      apiInstance.get
    );

  const onApproveSubscriptionRequests = async (ids: string[]) => {
    const body = {
      ids,
    };
    try {
      await apiInstance.post(
        `${API_ROUTES.ADD_SOCIETY_SUBSCRIBERS}/approve`,
        body
      );
      notificationInstance.success({
        title: 'Success',
        message: ids?.length > 1 ? 'Members accepted!.' : 'Member accepted!.',
      });
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onDeclineSubscriptionRequests = async (ids: string[]) => {
    const body = {
      ids,
    };
    try {
      await apiInstance.post(
        `${API_ROUTES.ADD_SOCIETY_SUBSCRIBERS}/reject`,
        body
      );
      notificationInstance.success({
        title: 'Success',
        message: ids?.length > 1 ? 'Members declined!.' : 'Member declined!.',
      });
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onDeleteMember = async (ids: string[]) => {
    const body = {
      ids,
    };
    try {
      await apiInstance.post(
        `${API_ROUTES.ADD_SOCIETY_SUBSCRIBERS}/remove_member`,
        body
      );
      notificationInstance.success({
        title: 'Success',
        message: 'Deleted Successfully.',
      });
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  const csvApiUrl = (title: string) => {
    const isStatusRequired = [
      CommunityTypes.SUBSCRIBED,
      CommunityTypes.UNSUBSCRIBED,
      CommunityTypes.INVITED,
      CommunityTypes.SUBSCRIPTION_REQUESTS,
    ].includes(title as CommunityTypes);

    if (isStatusRequired)
      return `${API_ROUTES.ADD_SOCIETY_SUBSCRIBERS}.csv?status=${title}`;
    return `${API_ROUTES.STUDENT_SOCIETY}/${title}.csv`;
  };

  const onCSVExport = async (
    title: CommunityTypes
  ): Promise<string | undefined> => {
    plainHeaders.set('Content-Type', 'text/csv');
    plainHeaders.set('Accept', 'text/csv');
    plainHeaders.set('Response-Type', 'text/csv');
    const { NEXT_PUBLIC_API_HOST_URL } = process.env;

    const path = `${NEXT_PUBLIC_API_HOST_URL}${csvApiUrl(title)}`;

    const fileTitle = title ? getFormattedFilename(title) : 'community-report';

    try {
      const response = await fetch(path, { headers: plainHeaders });
      const data = await response.text();
      const blob = new Blob([data], { type: 'data:text/csv;charset=utf-8,' });
      const blobURL = window.URL.createObjectURL(blob);
      const fileName = `${fileTitle}-Community
      -${format(new Date(), 'yyyy-MM-dd')}.csv`;
      saveAs(blobURL, fileName);
      return data;
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  const sendMessage = async (message: string, candidate_ids: string[]) => {
    const body = {
      message: {
        value: message,
      },
      messageable_ids: candidate_ids,
    };
    try {
      await apiInstance.post(`${API_ROUTES.STUDENT_SOCIETY_MESSAGES}`, body);
      notificationInstance.success({
        title: 'Success',
        message: 'Message sent!.',
      });
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  return {
    dashboardData,
    mutateCommunityDashboard,
    onApproveSubscriptionRequests,
    onDeclineSubscriptionRequests,
    onDeleteMember,
    onCSVExport,
    sendMessage,
  };
};
